import { AGRAVOS, DESCRICAO_AGRAVOS, DESCRICAO_PERFIS } from '@metaepi/shared';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';
import {
  ITENS_ADMINISTRACAO,
  ITENS_FASES,
  ITENS_PRINCIPAIS,
  itemVisivel,
  PERFIS_AGRAVOS,
  type ItemNavegacao,
} from '../../app/navegacao';
import { Cartao, CartaoDescricao, CartaoTitulo, SeloStatus } from '../../components/ui';
import { useSessao } from '../auth/contexto';
import { ResumoIndicadores } from '../indicadores/ResumoIndicadores';
import { IndicadorSaude } from '../sistema/IndicadorSaude';

function Cartoes({ itens, mostrarModulo }: { itens: ItemNavegacao[]; mostrarModulo: boolean }) {
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {itens.map((item) => (
        <li key={item.caminho}>
          <Cartao className="flex h-full flex-col gap-3">
            {mostrarModulo && (
              <SeloStatus tipo="info" className="self-start">{`Módulo ${item.modulo}`}</SeloStatus>
            )}
            <CartaoTitulo>{item.rotulo}</CartaoTitulo>
            <CartaoDescricao className="flex-1">{item.descricao}</CartaoDescricao>
            <Link
              to={item.caminho}
              className="inline-flex items-center gap-1 self-start rounded text-sm font-medium text-primaria hover:underline"
            >
              {mostrarModulo ? 'Ver detalhes' : 'Abrir'}
              <span className="sr-only"> de {item.rotulo}</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Cartao>
        </li>
      ))}
    </ul>
  );
}

export function PaginaInicio() {
  const { conta, pode } = useSessao();
  if (!conta) return null;
  const { perfil, nome } = conta.usuario;
  const visiveis = (itens: ItemNavegacao[]) => itens.filter((i) => itemVisivel(i, perfil, pode));
  const administracao = visiveis(ITENS_ADMINISTRACAO);
  const modulos = visiveis([...ITENS_FASES, ...ITENS_PRINCIPAIS]);
  const prontos = modulos.filter((i) => i.pronto);
  const proximos = modulos.filter((i) => !i.pronto).sort((a, b) => a.modulo - b.modulo);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-2xl font-bold text-texto sm:text-3xl">{`Olá, ${nome.split(' ')[0]}`}</h1>
        <p className="max-w-3xl text-texto-suave">
          {`Você entrou como ${DESCRICAO_PERFIS[perfil].rotulo.toLowerCase()}. ${DESCRICAO_PERFIS[perfil].descricao}`}
        </p>
        <div className="sm:hidden">
          <IndicadorSaude />
        </div>
      </section>

      {/* Módulo 8: o gestor do município abre o sistema já vendo os indicadores do ano */}
      {perfil === 'gestor' && <ResumoIndicadores />}

      {prontos.length > 0 && (
        <section aria-labelledby="titulo-atendimento" className="flex flex-col gap-3">
          <h2 id="titulo-atendimento" className="text-lg font-semibold text-texto">
            Atendimento
          </h2>
          <Cartoes itens={prontos} mostrarModulo={false} />
        </section>
      )}

      {administracao.length > 0 && (
        <section aria-labelledby="titulo-admin" className="flex flex-col gap-3">
          <h2 id="titulo-admin" className="text-lg font-semibold text-texto">
            Administração
          </h2>
          <Cartoes itens={administracao} mostrarModulo={false} />
        </section>
      )}

      {PERFIS_AGRAVOS.includes(perfil) && (
        <section aria-labelledby="titulo-agravos" className="flex flex-col gap-3">
          <h2 id="titulo-agravos" className="text-lg font-semibold text-texto">
            Agravos acompanhados
          </h2>
          <ul className="flex flex-wrap gap-2">
            {AGRAVOS.map((agravo) => (
              <li
                key={agravo}
                className="flex items-center gap-2 rounded-lg border border-borda bg-superficie px-3 py-2"
              >
                <span className="text-sm font-medium text-texto">
                  {DESCRICAO_AGRAVOS[agravo].rotulo}
                </span>
                {conta.agravosDisponiveis[agravo] ? (
                  <SeloStatus tipo="info">Em construção</SeloStatus>
                ) : (
                  <SeloStatus tipo="desenvolvimento">Em desenvolvimento</SeloStatus>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {proximos.length > 0 && (
        <section aria-labelledby="titulo-modulos" className="flex flex-col gap-3">
          <h2 id="titulo-modulos" className="text-lg font-semibold text-texto">
            O que vem a seguir
          </h2>
          <Cartoes itens={proximos} mostrarModulo />
        </section>
      )}
    </div>
  );
}
